import Breadcrumbs from 'Components/Breadcrumbs'
import CustomButton from 'Components/CustomButton'
import ListActions from 'Components/ListActions'
import ListSearch from 'Components/ListSearch'
import PageTitle from 'Components/PageTitle'
import { WithLoader } from 'Components/WithLoader'
import { CatalogList } from 'Services/ProductManagement/Features/Catalog/Components/CatalogList'
import { useCatalogsWithParams } from 'Services/ProductManagement/Features/Catalog/Hooks'
import TariffCatalogDrawer from 'Services/ProductManagement/Features/Tariff/Components/TariffCatalogDrawer'
import { useSelectedTariff } from 'Services/ProductManagement/Features/Tariff/Hooks'
import { PRODUCT_MANAGEMENT_ROOT_URL, ROWS_PER_PAGE_DEFAULT } from 'Util/constants'
import useListFunctions from 'Util/hooks/useListFunctions'
import AddIcon from '@mui/icons-material/Add'
import { Box } from '@mui/material'
import { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { t } from 'ttag'

function TariffCatalogListPage() {
  const baseUrl = `${PRODUCT_MANAGEMENT_ROOT_URL}/tariffs`
  const navigate = useNavigate()
  const { id: tariffId } = useParams()

  const { singleTariff, isSingleTariffLoading } = useSelectedTariff(tariffId)
  const {
    catalogs,
    isFetching: isCatalogsFetching,
    searchQueryParams,
    setSearchQueryParams,
    totalResults,
    selectedCatalogId,
    setSelectedCatalogId
  } = useCatalogsWithParams()

  const [isDrawerOpen, setIsDrawerOpen] = useState(!!selectedCatalogId)

  const { searchText, handleChangePage, handleRequestSort, handleSearchTextChange, handleSearch } =
    useListFunctions({
      searchQueryParams,
      setSearchQueryParams
    })

  const breadcrumbs = [
    {
      title: t`Home`,
      link: PRODUCT_MANAGEMENT_ROOT_URL
    },
    {
      title: t`Tariffs`,
      link: baseUrl
    },
    {
      title: singleTariff ? singleTariff.description : ''
    },
    {
      title: t`Tariff sales channels`
    }
  ]

  const handleDrawerClose = () => {
    setSelectedCatalogId('')
    setIsDrawerOpen(false)
  }

  const handleRowClick = (id: string) => {
    setSelectedCatalogId(id)
    setIsDrawerOpen(true)
  }

  useEffect(() => {
    setSearchQueryParams(prevParams => ({ ...prevParams, tariff_id: tariffId }))
  }, [tariffId])

  return (
    <Box sx={{ width: '100%' }}>
      <Box sx={{ mb: 2 }}>
        <Breadcrumbs items={breadcrumbs} />
        <PageTitle>{t`Tariff sales channels`}</PageTitle>
      </Box>
      <ListActions>
        <CustomButton
          onClick={() => navigate(`${baseUrl}/${tariffId}/sales-channels/create/`)}
          size="small"
          startIcon={<AddIcon />}
        >
          {t`Create new sales channel`}
        </CustomButton>
        <ListSearch
          searchText={searchText}
          handleSearchTextChange={handleSearchTextChange}
          handleSearch={handleSearch}
          placeholder={t`Search by name or code`}
          sx={{ marginLeft: 'auto' }}
        />
      </ListActions>
      <Box pt={1}>
        <CatalogList
          rows={catalogs}
          isLoading={isCatalogsFetching || isSingleTariffLoading}
          params={searchQueryParams}
          handleRequestSort={handleRequestSort}
          handleChangePage={handleChangePage}
          selectedCatalogId={selectedCatalogId}
          onRowClick={handleRowClick}
          count={totalResults}
          page={
            searchQueryParams.start_index
              ? Number.parseInt(searchQueryParams.start_index, 10) / ROWS_PER_PAGE_DEFAULT
              : 0
          }
        />
      </Box>
      <WithLoader loading={isSingleTariffLoading}>
        {singleTariff && (
          <TariffCatalogDrawer
            open={isDrawerOpen}
            handleClose={handleDrawerClose}
            tariff={singleTariff}
            selectedCatalogId={selectedCatalogId}
          />
        )}
      </WithLoader>
    </Box>
  )
}

export default TariffCatalogListPage
